import React, { FunctionComponent, useState } from 'react'
import {
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Flex,
  Text,
  Spinner,
  Box,
  Button
} from '@chakra-ui/react'
import { DocumentPlusIcon } from '@heroicons/react/20/solid'
import {
  UNDERWRITING_METHODOLOGIES,
  RENTAL_APPLICATION_STATUSES as STATUS
} from '@homevest/utils/rental-applications'

import ApplicationsTable from 'components/RentalApplications/ApplicationsTable'
import FilterTab from 'components/RentalApplications/FilterTab'
import { Comparators } from 'lib/hasura-comparators'
import {
  Application,
  STATUS_MAP,
  SAFERENT_STATUS_MAP
} from 'components/RentalApplications/applications-table-utils'
import SidePeek from 'components/RentalApplications/SidePeek'
import { Link } from 'ui'
import CreateApplicationModal from './Applications/CreateApplicationModal'

const FILTERED_OUT_STATUSES = [
  STATUS.PENDING_ADDITIONAL_UNDERWRITING_INFORMATION,
  STATUS.PENDING_PROPERTY_PURCHASE,
  STATUS.HOLD_FOR_NEW_LEASE,
  STATUS.CANCELED
]

const TAB_CONFIG = [
  { id: 'all', label: 'All', partial: null },
  ...Object.keys(STATUS_MAP)
    .filter((id) => !FILTERED_OUT_STATUSES.some((s) => s === id))
    .map((id) => ({
      id,
      label: STATUS_MAP[id as keyof typeof STATUS_MAP],
      partial: { status: Comparators.isIn([id]) }
    }))
]

const SAFERENT_TAB_CONFIG = [
  { id: 'all', label: 'All', partial: null },
  ...Object.keys(SAFERENT_STATUS_MAP)
    .filter((id) => !FILTERED_OUT_STATUSES.some((s) => s === id))
    .map((id) => ({
      id,
      label: SAFERENT_STATUS_MAP[id as keyof typeof SAFERENT_STATUS_MAP],
      partial: { status: Comparators.isIn([id]) }
    }))
]

const RentalApplications: FunctionComponent<React.PropsWithChildren<unknown>> = () => {
  const [methodologyTab, setMethodologyTab] = useState<number>(0)
  const [statusTab, setStatusTab] = useState<number>(0)
  const [count, setCount] = useState<number>()
  const [peeked, setPeeked] = useState<Application>()
  const [createNew, setCreateNew] = useState(false)

  const methodology =
    methodologyTab === 0 ? UNDERWRITING_METHODOLOGIES.SAFERENT : UNDERWRITING_METHODOLOGIES.UPANDUP
  const currentTabConfig =
    methodology === UNDERWRITING_METHODOLOGIES.SAFERENT ? SAFERENT_TAB_CONFIG : TAB_CONFIG

  return (
    <Flex direction='column' p={8} gap={4}>
      <Flex direction='row' align='center' justify='space-between'>
        <Link to='/legacy/applications' fontSize='sm' color='gray.500' useRouter>
          View legacy rental applications page
        </Link>
        <Button
          variant='solid'
          colorScheme='teal'
          size='xs'
          onClick={() => setCreateNew(true)}
          leftIcon={
            <Box h={4} w={4}>
              <DocumentPlusIcon />
            </Box>
          }
        >
          Create new
        </Button>
      </Flex>

      {/* Methodology Tabs */}
      <Box overflowX='auto' p={1}>
        <Tabs
          size='sm'
          colorScheme='teal'
          variant='solid-rounded'
          align='center'
          index={methodologyTab}
          onChange={(index) => {
            setMethodologyTab(index)
            setStatusTab(0) // Reset status tab when switching methodology
          }}
        >
          <TabList>
            <Tab>Saferent Applications</Tab>
            <Tab>Up&Up Applications</Tab>
          </TabList>

          <TabPanels>
            {/* Saferent Tab Panel */}
            <TabPanel p={0} pt={4}>
              <Box overflowX='auto' p={1}>
                <Tabs colorScheme='teal' index={statusTab} onChange={setStatusTab}>
                  <TabList>
                    {SAFERENT_TAB_CONFIG.map((item, idx) => (
                      <FilterTab
                        key={item.id}
                        label={item.label}
                        partial={item.partial}
                        active={statusTab === idx}
                        methodology='saferent'
                      />
                    ))}
                  </TabList>
                </Tabs>
              </Box>
            </TabPanel>

            {/* Up&Up Tab Panel */}
            <TabPanel p={0} pt={4}>
              <Box overflowX='auto' p={1}>
                <Tabs colorScheme='teal' index={statusTab} onChange={setStatusTab}>
                  <TabList>
                    {TAB_CONFIG.map((item, idx) => (
                      <FilterTab
                        key={item.id}
                        label={item.label}
                        partial={item.partial}
                        active={statusTab === idx}
                        methodology='upandup'
                      />
                    ))}
                  </TabList>
                </Tabs>
              </Box>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
      <Flex direction='row' align='center' gap={2}>
        {count === undefined && <Spinner mr={3} />}
        <Text fontWeight={600} fontSize='3xl'>
          {count} applications
        </Text>
      </Flex>
      <ApplicationsTable
        selectedTab={currentTabConfig[statusTab].id}
        methodology={methodology}
        onPeek={setPeeked}
        onCount={(result) => {
          setCount(result.fetching || result.count === undefined ? undefined : result.count)
        }}
      />
      {peeked && <SidePeek appId={peeked.id} onClose={() => setPeeked(undefined)} />}
      {createNew && <CreateApplicationModal setShowCreateModal={setCreateNew} />}
    </Flex>
  )
}

export default RentalApplications
